/**
 * useStorageImage — show a Storage image by reading its BYTES under the Storage rules
 * (gallery blueprint U2).
 *
 * 🔴 NEVER `getDownloadURL` here. It mints and persists a token on the object, and the
 * token URL then works for anyone holding the string, forever, outside storage.rules. The
 * full reasoning and measurement is in lib/goformz/readArchive.ts. `getBlob` is an
 * authenticated read that stamps nothing.
 *
 * The Blob is cached in React Query by path (so a thumbnail shown in three places is read
 * once); each mounted component makes its own object URL and revokes it on unmount.
 *
 * `cache` is an optional persistent layer (the tech app's offline cache, U7). A cache that
 * throws is ignored: the image still loads from the network.
 *
 * @example
 * const { url, isLoading, error } = useStorageImage(summary.primaryThumbPath);
 */
import { useEffect, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { getBlob, ref } from 'firebase/storage';
import { storage } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';

export interface StorageImageCache {
  get(path: string): Promise<Blob | null>;
  put(path: string, blob: Blob): Promise<void> | void;
}

export interface StorageImageState {
  url: string | null;
  isLoading: boolean;
  error: Error | null;
}

/** Keyed by org as well as path, so a user who switches org never sees the other org's cache. */
export const storageImageKeys = {
  all: ['storageImage'] as const,
  blob: (organizationId: string, path: string) => [...storageImageKeys.all, organizationId, path] as const,
};

/**
 * How long an unused image stays in memory. Gallery display/thumb objects are small and
 * shown on several screens, so they stay; a report ORIGINAL (~1920px) shown in the library
 * drawer is released quickly — a drawer scrolled through a busy unit would otherwise hold
 * hundreds of MB.
 */
export const IMAGE_RETENTION = {
  long: 1000 * 60 * 30,
  short: 1000 * 60,
} as const;

async function readCached(cache: StorageImageCache | undefined, path: string): Promise<Blob | null> {
  if (!cache) return null;
  try {
    return await cache.get(path);
  } catch {
    return null;
  }
}

/** Fetch a Storage object's bytes, via the optional cache. Exported for prefetching (U7). */
export async function fetchStorageBlob(path: string, cache?: StorageImageCache): Promise<Blob> {
  const cached = await readCached(cache, path);
  if (cached) return cached;
  const blob = await getBlob(ref(storage, path));
  if (cache) {
    try {
      await cache.put(path, blob);
    } catch {
      // A full or unavailable cache must not fail the image.
    }
  }
  return blob;
}

// Storage objects shown here are never rewritten in place (gallery copies are write-once, D9).
const BLOB_STALE = Infinity;

/**
 * Warm the image cache for a path the viewer is likely to show next (a neighbour in a photo
 * viewer): fetches the bytes with short retention and creates no object URL, so nothing is
 * minted that nobody displays. `useStorageImage` on the same path then reads it from cache.
 */
export function usePrefetchStorageImage(path: string | null | undefined, cache?: StorageImageCache): void {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  useQuery({
    queryKey: storageImageKeys.blob(orgId, path ?? ''),
    queryFn: () => fetchStorageBlob(path as string, cache),
    enabled: !!path,
    staleTime: BLOB_STALE,
    gcTime: IMAGE_RETENTION.short,
  });
}

export function useStorageImage(
  path: string | null | undefined,
  options: { cache?: StorageImageCache; retention?: keyof typeof IMAGE_RETENTION } = {}
): StorageImageState {
  const { cache, retention = 'long' } = options;
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const query = useQuery({
    queryKey: storageImageKeys.blob(orgId, path ?? ''),
    queryFn: () => fetchStorageBlob(path as string, cache),
    enabled: !!path,
    staleTime: BLOB_STALE,
    gcTime: IMAGE_RETENTION[retention],
  });

  const [url, setUrl] = useState<string | null>(null);
  const blob = query.data;
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  if (!path) return { url: null, isLoading: false, error: null };
  return {
    url: blob ? url : null,
    isLoading: query.isLoading || (!!blob && url === null),
    error: (query.error as Error | null) ?? null,
  };
}

/**
 * Many images at once (a thumbnail strip): `{ [path]: objectUrl }` for every path loaded so
 * far. Shares the per-path Blob cache with useStorageImage; every URL is revoked when the
 * set changes or the component unmounts.
 */
export function useStorageImages(
  paths: readonly string[],
  options: { cache?: StorageImageCache } = {}
): Record<string, string> {
  const { cache } = options;
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const results = useQueries({
    queries: paths.map((path) => ({
      queryKey: storageImageKeys.blob(orgId, path),
      queryFn: () => fetchStorageBlob(path, cache),
      staleTime: BLOB_STALE,
      gcTime: IMAGE_RETENTION.long,
    })),
  });
  // Changes only when a path is added, removed or gets new bytes, so URLs are not remade per render.
  const fingerprint = paths.map((p, i) => `${p}@${results[i]?.dataUpdatedAt ?? 0}`).join('|');

  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    const made: Record<string, string> = {};
    results.forEach((r, i) => {
      if (r.data) made[paths[i]] = URL.createObjectURL(r.data);
    });
    setUrls(made);
    return () => Object.values(made).forEach((u) => URL.revokeObjectURL(u));
    // `fingerprint` captures every input that matters; `results`/`paths` identities change per render.
  }, [fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps
  return urls;
}
